import { useMemo } from 'preact/compat';
import { twMerge } from 'tailwind-merge';

import type { Device, Feature } from '~/utils/content-types';
import { formatCurrency } from '~/utils/formatCurrency';
import { isPresent } from './product-finder/utils';

type RatedDevice = Device & { match?: number; selected?: Map<string, Feature[]> };

export interface ShopListItemProps {
  device: RatedDevice;
  shopItemClass?: string;
}

export default function ShopListItem({ device, shopItemClass: shopItemClassName }: ShopListItemProps) {
  const {
    match,
    data: { title, description, url, image, price, compareAtPrice },
  } = device;

  const discount = useMemo(
    () =>
      compareAtPrice && price && compareAtPrice > price
        ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
        : null,
    [compareAtPrice, price]
  );

  return (
    <a
      className={twMerge(
        'w-[292px] md:w-[356px] rounded-xl bg-accent drop-shadow-lg overflow-hidden',
        'flex-shrink-0 flex flex-col px-6 py-4',
        shopItemClassName
      )}
      href={url}
      button-type="shop"
      product-name={title}
      product-url={url}
    >
      {match != null && (
        <div className="h-8 flex justify-between items-center space-x-4 md:space-x-8">
          <IconGauge className="w-20" match={match} />

          <div className="text-xs font-light tracking-tight">
            Diese Skala zeigt, wie gut das Gerät deine Anforderungen erfüllt.
          </div>
        </div>
      )}

      <img
        src={image}
        loading="lazy"
        decoding="async"
        alt="hero"
        className="object-scale-down w-full h-44 md:h-52"
      />

      <h3 className="font-semibold text-base leading-tight select-none line-clamp-3 mb-4">{title}</h3>

      <div className="mb-4">
        {device.selected?.size ? (
          <div className="grid grid-flow-row-dense grid-cols-2 gap-1">
            {Array.from(device.selected).map(([title, features]) => (
              <div className="flex flex-row items-center space-x-2">
                <div className="flex justify-center items-center">
                  {features.some((x) => isPresent(x, device)) ? (
                    <PositiveIcon className="size-5" />
                  ) : (
                    <NeutralIcon className="size-5" />
                  )}
                </div>

                <span className="text-xs leading-none line-clamp-2">{title}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm" dangerouslySetInnerHTML={{ __html: description ?? '' }} />
        )}
      </div>

      <div className="flex-grow" />

      <div class="flex justify-between items-center">
        <div className="flex-shrink block">
          <h5 className="space-x-2 flex items-center">
            {!!compareAtPrice && <span className="text-sm line-through">{formatCurrency(compareAtPrice)}</span>}
            {!!discount && (
              <span className="w-12 py-1 bg-[#ef1f1f] rounded-full text-xs text-white text-center align-middle">
                -{discount}%
              </span>
            )}
          </h5>

          {!!price && <h4 className="flex-shrink block text-xl font-semibold">{formatCurrency(price)}</h4>}
        </div>

        <div className="btn-primary text-xs shadow-md">Zum Artikel</div>
      </div>
    </a>
  );
}

const PositiveIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 19" className={className}>
    <g id="surface27144" transform="translate(-4.688 -4.688)">
      <path
        d="M14.188,4.688a9.5,9.5,0,1,0,9.5,9.5,8.338,8.338,0,0,0-.512-3l-1.169,1.169a7.362,7.362,0,0,1,.219,1.827,8.106,8.106,0,1,1-2.485-5.773l1.023-1.023A9.279,9.279,0,0,0,14.188,4.688Zm8.258,3.288-8.258,8.258-3.142-3.142-1.023,1.1,4.165,4.165,9.281-9.281Zm0,0"
        fill="#0368ab"
      />
    </g>
  </svg>
);

const NeutralIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={className}>
    <circle fill="none" stroke="#0368ab" stroke-width={7} r="45" cx="50" cy="50" />
  </svg>
);

const IconGauge = ({ className, match }: { className: string; match: number }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 540.68 263.12">
    <g>
      <g>
        <path
          fill="#ee3c39"
          d="M0,262.08l90.21.18c.98-22.07,6.04-43.44,14.57-63.19l-81.18-39.29C9.33,191.55,1.08,226.24,0,262.08Z"
        />
        <path
          fill="#f37637"
          d="M30.46,145.64l81.19,39.29c10.29-19.09,24-36.29,40.5-50.64l-56.11-70.64c-27.09,22.87-49.33,50.8-65.57,81.98Z"
        />
        <path
          fill="#f9a534"
          d="M108.34,53.88l56.11,70.64c17.2-12.5,36.88-22.12,58.49-27.98l-19.9-87.97c-35.25,9.03-67.17,24.68-94.7,45.31Z"
        />
        <path
          fill="#fcd834"
          d="M218.36,5.1l19.9,87.97c22.03-4.01,43.94-3.8,64.84.08l20.24-87.91c-33.73-6.77-69.27-7.16-104.98-.14Z"
        />
        <path
          fill="#c3d84b"
          d="M338.65,8.78l-20.25,87.91c21.07,5.85,40.85,15.47,58.36,28.27l56.37-70.4c-28.08-21.16-60.18-36.8-94.48-45.78Z"
        />
        <path
          fill="#71be46"
          d="M445.4,64.36l-56.37,70.4c16.2,14.16,29.96,31.27,40.35,50.77l81.35-38.99c-16.4-31.89-38.78-59.65-65.34-82.18Z"
        />
        <path
          fill="#1bb04b"
          d="M534.06,210.79c-3.96-17.52-9.55-34.25-16.55-50.08l-81.35,38.99c4.18,9.84,7.55,20.18,9.99,30.98,2.44,10.8,3.86,21.59,4.31,32.27l90.21.18c-.49-17.3-2.65-34.81-6.62-52.33Z"
        />
      </g>
      <g transform={`rotate(${match * 180}, 270.81, 235.89)`}>
        <path
          fill="#000"
          d="M297.28,235.89c0,14.61-11.85,26.46-26.46,26.46-8.91,0-16.79-4.41-21.59-11.15l-197.16-15.31,197.16-15.31c4.79-6.75,12.68-11.15,21.59-11.15,14.61,0,26.46,11.84,26.46,26.46Z"
        />
        <circle fill="#fff" cx="270.81" cy="235.89" r="9.1" />
      </g>
    </g>
  </svg>
);
